import React, { useEffect, useState, useMemo, memo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "@/store/store";
import ViewLocation from "@/components/ViewLocation/ViewLocation";
import { SearchableSelect } from "@/components/SelectModal/SearchableSelect";
import { ButtonLoader } from "@/components/Loader/ButtonLoader";
import { getUserGeofenceAreaById } from "@/store/slices/GeofenceManagementSlice";
import toast from "@/lib/toast";

interface IManageGeofence {
  modalOpen: boolean;
  closeModal: () => void;
  saveModal: (payload: any) => void;
  selectedUser: any;
}

const getTypeStyle = (type?: string) => {
  const t = (type || "").toLowerCase();
  if (t.includes("office")) {
    return {
      bg: "#f0f4ff",
      color: "#5d78ff",
      border: "#e0e7ff",
    };
  }
  if (t.includes("home")) {
    return {
      bg: "#ecfdf5",
      color: "#10b981",
      border: "#d1fae5",
    };
  }
  if (t.includes("client")) {
    return {
      bg: "#fffbeb",
      color: "#d97706",
      border: "#fef3c7",
    };
  }
  return {
    bg: "#f8fafc",
    color: "#64748b",
    border: "#e2e8f0",
  };
};

const formatCoordinates = (lat?: number | string, lng?: number | string, range?: number) => {
  const numLat = Number(lat);
  const numLng = Number(lng);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLng)) return null;

  const latDir = numLat >= 0 ? "° N" : "° S";
  const lngDir = numLng >= 0 ? "° E" : "° W";

  return `${Math.abs(numLat).toFixed(4)}${latDir}, ${Math.abs(numLng).toFixed(4)}${lngDir}${range ? ` · Radius ${range}m` : ""}`;
};

const ManageGeofence: React.FC<IManageGeofence> = ({
  modalOpen,
  closeModal,
  saveModal,
  selectedUser,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [location, setLocation] = useState({ lat: "", lng: "" });
  const [selectedGeofenceTitle, setSelectedGeofenceTitle] = useState<string>("");
  const [modalOpenViewLocation, setModalOpenViewLocation] = useState(false);
  const [loadingLocationId, setLoadingLocationId] = useState<number | null>(null);

  const [punchInGeofences, setPunchInGeofences] = useState<any[]>([]);
  const [punchOutGeofences, setPunchOutGeofences] = useState<any[]>([]);

  const [originalPunchIn, setOriginalPunchIn] = useState<any[]>([]);
  const [originalPunchOut, setOriginalPunchOut] = useState<any[]>([]);

  // Fetch the geofence options from redux
  const { geoFencingArea } = useSelector((state: any) => state.team);
  const { loading } = useSelector((state: any) => state.usergeofenceManagement);

  const enrichGeofences = (geofences: any[]) =>
    (geofences || []).map((item: any) =>
      geoFencingArea?.find((area: any) => area.Id === item.Id) || item
    );

  useEffect(() => {
    if (modalOpen && selectedUser) {
      const punchIn = enrichGeofences(selectedUser?.PunchInGeoFences || []);
      const punchOut = enrichGeofences(selectedUser?.PunchOutGeoFences || []);

      setPunchInGeofences(punchIn);
      setPunchOutGeofences(punchOut);

      setOriginalPunchIn(punchIn);
      setOriginalPunchOut(punchOut);
    }
  }, [modalOpen, selectedUser, geoFencingArea]);

  const availablePunchInOptions = useMemo(() => {
    return (geoFencingArea || []).filter(
      (option: any) => !punchInGeofences.some((selected) => selected.Id === option.Id)
    );
  }, [geoFencingArea, punchInGeofences]);

  const availablePunchOutOptions = useMemo(() => {
    return (geoFencingArea || []).filter(
      (option: any) => !punchOutGeofences.some((selected) => selected.Id === option.Id)
    );
  }, [geoFencingArea, punchOutGeofences]);

  const punchInSelectOptions = useMemo(() => {
    return availablePunchInOptions.map((item: any) => ({
      ...item,
      DisplayName: item.GeoFenceTypeName ? `${item.Name} (${item.GeoFenceTypeName})` : item.Name,
    }));
  }, [availablePunchInOptions]);

  const punchOutSelectOptions = useMemo(() => {
    return availablePunchOutOptions.map((item: any) => ({
      ...item,
      DisplayName: item.GeoFenceTypeName ? `${item.Name} (${item.GeoFenceTypeName})` : item.Name,
    }));
  }, [availablePunchOutOptions]);

  const totalCount = punchInGeofences.length + punchOutGeofences.length;

  const handleOnClickLocation = async (item: any) => {
    try {
      setSelectedGeofenceTitle(
        `${item.Name} ${item?.GeoFenceTypeName ? `(${item?.GeoFenceTypeName})` : ""}`
      );

      // 1. Direct coordinates on object
      if (item?.Latitude != null && item?.Longitude != null) {
        setLocation({
          lat: String(item.Latitude),
          lng: String(item.Longitude),
        });
        setModalOpenViewLocation(true);
        return;
      }

      // 2. Check local geoFencingArea store
      const localArea = geoFencingArea?.find((g: any) => g.Id === item.Id);
      if (localArea?.Latitude != null && localArea?.Longitude != null) {
        setLocation({
          lat: String(localArea.Latitude),
          lng: String(localArea.Longitude),
        });
        setModalOpenViewLocation(true);
        return;
      }

      // 3. Fetch from API
      setLoadingLocationId(item.Id);
      const area: any = await dispatch(getUserGeofenceAreaById(item.Id)).unwrap();
      if (area?.Latitude != null && area?.Longitude != null) {
        setLocation({
          lat: String(area.Latitude),
          lng: String(area.Longitude),
        });
        setModalOpenViewLocation(true);
        return;
      }

      toast.error("Coordinates not available for this geofence");
    } catch (error: any) {
      toast.error(error || "Failed to load geofence location");
    } finally {
      setLoadingLocationId(null);
    }
  };

  const removePunchIn = (id: any) => {
    setPunchInGeofences((prev) => prev.filter((item) => item.Id !== id));
  };

  const removePunchOut = (id: any) => {
    setPunchOutGeofences((prev) => prev.filter((item) => item.Id !== id));
  };

  const handleAddPunchIn = (selectedId: any) => {
    if (!selectedId) return;
    const item = geoFencingArea?.find((g: any) => g.Id === Number(selectedId));
    if (item && !punchInGeofences.some((g) => g.Id === item.Id)) {
      setPunchInGeofences((prev) => [...prev, item]);
    }
  };

  const handleAddPunchOut = (selectedId: any) => {
    if (!selectedId) return;
    const item = geoFencingArea?.find((g: any) => g.Id === Number(selectedId));
    if (item && !punchOutGeofences.some((g) => g.Id === item.Id)) {
      setPunchOutGeofences((prev) => [...prev, item]);
    }
  };

  const buildGeoFencePayload = (userId: number, punchInList: any[], punchOutList: any[]) => {
    const count = Math.max(punchInList.length, punchOutList.length);
    const geoFences = [];

    for (let i = 0; i < count; i++) {
      const inItem = punchInList[i];
      const outItem = punchOutList[i];

      geoFences.push({
        PunchInGeoFenceId: inItem?.Id ?? null,
        PunchOutGeoFenceId: outItem?.Id ?? null,
      });
    }

    return {
      UserIds: [userId],
      GeoFences: geoFences,
    };
  };

  const handleSubmitGeofence = () => {
    const userId = Number(selectedUser?.UserId);

    const addedPunchIn = punchInGeofences.filter(
      (x) => !originalPunchIn.some((y) => y.Id === x.Id)
    );
    const removedPunchIn = originalPunchIn.filter(
      (x) => !punchInGeofences.some((y) => y.Id === x.Id)
    );

    const addedPunchOut = punchOutGeofences.filter(
      (x) => !originalPunchOut.some((y) => y.Id === x.Id)
    );
    const removedPunchOut = originalPunchOut.filter(
      (x) => !punchOutGeofences.some((y) => y.Id === x.Id)
    );

    const addPayload = buildGeoFencePayload(userId, addedPunchIn, addedPunchOut);
    const removePayload = buildGeoFencePayload(userId, removedPunchIn, removedPunchOut);

    saveModal({
      addPayload,
      removePayload,
    });
  };

  const renderGeofenceList = (items: any[], onRemove: (id: any) => void, typeLabel: string) => {
    if (items.length === 0) {
      return (
        <Box
          sx={{
            py: 2,
            px: 2,
            backgroundColor: "#fbfcfe",
            borderRadius: "8px",
            border: "1px dashed #e2e8f0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            color: "#94a3b8",
          }}
        >
          <LocationOffOutlinedIcon sx={{ fontSize: 20, color: "#cbd5e1" }} />
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              fontWeight: 400,
              color: "#94a3b8",
            }}
          >
            No {typeLabel.toLowerCase()} geofences assigned
          </Typography>
        </Box>
      );
    }

    return (
      <div className="flex flex-col gap-1.5">
        {items.map((item: any, index: number) => {
          const typeStyle = getTypeStyle(item?.GeoFenceTypeName);
          const isItemLoading = loadingLocationId === item.Id;
          const coordsText = formatCoordinates(item.Latitude, item.Longitude, item.Range);

          return (
            <Box
              key={`${item.Id || index}-${typeLabel}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                p: "8px 12px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #edf0f7",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  backgroundColor: "#fbfcfe",
                },
              }}
            >
              {/* Left Side: Pin Icon + Name + Type badge + Coords */}
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <LocationOnOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: "#5d78ff",
                    flexShrink: 0,
                  }}
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Typography
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 500,
                        fontSize: "12.5px",
                        color: "#1e2433",
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.Name}
                    </Typography>

                    {item?.GeoFenceTypeName && (
                      <Chip
                        label={item.GeoFenceTypeName}
                        size="small"
                        sx={{
                          height: "20px",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "10.5px",
                          fontWeight: 400,
                          backgroundColor: typeStyle.bg,
                          color: typeStyle.color,
                          border: `1px solid ${typeStyle.border}`,
                          borderRadius: "4px",
                          px: 0.5,
                        }}
                      />
                    )}
                  </div>

                  {coordsText && (
                    <Typography
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "11px",
                        color: "#8c98a9",
                        mt: 0.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {coordsText}
                    </Typography>
                  )}
                </div>
              </div>

              {/* Right Side: Map preview + Delete */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Tooltip title="View on Map">
                  <IconButton
                    size="small"
                    onClick={() => handleOnClickLocation(item)}
                    sx={{
                      width: 26,
                      height: 26,
                      color: "#94a3b8",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        color: "#5d78ff",
                        backgroundColor: "#eef2ff",
                      },
                    }}
                  >
                    {isItemLoading ? (
                      <span className="inline-block w-3 h-3 border-2 border-[#5d78ff] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <MapOutlinedIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    onClick={() => onRemove(item.Id)}
                    sx={{
                      width: 26,
                      height: 26,
                      color: "#94a3b8",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        color: "#ef4444",
                        backgroundColor: "#fef2f2",
                      },
                    }}
                  >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </div>
            </Box>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        fullWidth
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: "480px",
              maxWidth: "94vw",
              borderRadius: "14px",
              overflow: "hidden",
              border: "1px solid #e9edf5",
              boxShadow: "0 10px 30px rgba(38, 46, 77, 0.12)",
            },
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid #edf0f7",
            background: "linear-gradient(180deg, #fafbfe 0%, #ffffff 100%)",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-[#eef2ff] text-[#5d78ff] flex items-center justify-center">
                <PinDropOutlinedIcon sx={{ fontSize: 17 }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: "14.5px",
                      color: "#1e2433",
                      lineHeight: 1.25,
                    }}
                  >
                    Manage Geofences
                  </Typography>
                  <span className="text-[11px] font-medium text-[#5d78ff] bg-[#eef2ff] px-2 py-0.2 rounded-full">
                    {totalCount} {totalCount === 1 ? "area" : "areas"}
                  </span>
                </div>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    color: "#64748b",
                    mt: 0.2,
                  }}
                >
                  {selectedUser?.UserName || "Selected User"}
                </Typography>
              </div>
            </div>

            <IconButton
              size="small"
              onClick={closeModal}
              sx={{
                color: "#94a3b8",
                "&:hover": { color: "#1e2433", backgroundColor: "#f1f5f9" },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </div>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          sx={{
            px: 2.5,
            py: 2,
            backgroundColor: "#ffffff",
            maxHeight: "65vh",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* Punch In Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <div className="flex items-center justify-between">
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11.5px",
                  fontWeight: 500,
                  color: "#64748b",
                }}
              >
                Punch In Geofences
              </Typography>
              <span className="text-[11px] font-[Poppins] text-[#64748b] bg-[#f1f5f9] px-1.5 py-0.2 rounded">
                {punchInGeofences.length}
              </span>
            </div>

            <SearchableSelect
              value=""
              placeholder="Select punch in geofence to add..."
              searchPlaceholder="Search geofences..."
              options={punchInSelectOptions}
              idKey="Id"
              labelKey="DisplayName"
              onChange={handleAddPunchIn}
              disabled={punchInSelectOptions.length === 0}
              sx={{
                height: "38px",
                fontSize: "12.5px",
                borderRadius: "8px",
                backgroundColor: "#fafbfe",
              }}
            />

            {renderGeofenceList(punchInGeofences, removePunchIn, "Punch In")}
          </Box>

          {/* Punch Out Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <div className="flex items-center justify-between">
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11.5px",
                  fontWeight: 500,
                  color: "#64748b",
                }}
              >
                Punch Out Geofences
              </Typography>
              <span className="text-[11px] font-[Poppins] text-[#64748b] bg-[#f1f5f9] px-1.5 py-0.2 rounded">
                {punchOutGeofences.length}
              </span>
            </div>

            <SearchableSelect
              value=""
              placeholder="Select punch out geofence to add..."
              searchPlaceholder="Search geofences..."
              options={punchOutSelectOptions}
              idKey="Id"
              labelKey="DisplayName"
              onChange={handleAddPunchOut}
              disabled={punchOutSelectOptions.length === 0}
              sx={{
                height: "38px",
                fontSize: "12.5px",
                borderRadius: "8px",
                backgroundColor: "#fafbfe",
              }}
            />

            {renderGeofenceList(punchOutGeofences, removePunchOut, "Punch Out")}
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #edf0f7",
            backgroundColor: "#fafbfe",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="text"
            onClick={closeModal}
            disabled={loading?.userGeofences}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "none",
              color: "#64748b",
              borderRadius: "8px",
              px: 2,
              "&:hover": { backgroundColor: "#f1f5f9" },
            }}
          >
            Cancel
          </Button>

          <ButtonLoader
            variant="contained"
            loading={loading?.userGeofences}
            onClick={handleSubmitGeofence}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "none",
              backgroundColor: "#5d78ff",
              color: "#ffffff",
              borderRadius: "8px",
              px: 2.5,
              height: "34px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#4863e6",
                boxShadow: "none",
              },
            }}
          >
            Save
          </ButtonLoader>
        </DialogActions>
      </Dialog>

      {/* Submodal for Map View */}
      {modalOpenViewLocation && (
        <ViewLocation
          title={selectedGeofenceTitle || "Geofence Location"}
          width="650px"
          modalOpen={modalOpenViewLocation}
          closeModal={() => {
            setModalOpenViewLocation(false);
            setSelectedGeofenceTitle("");
          }}
          lat={location.lat}
          lng={location.lng}
        />
      )}
    </>
  );
};

export default memo(ManageGeofence);